import React, { useEffect, useState } from "react";
import { ActivityHistory, MasterDrawer } from "../Shared/master";
import { LOG_TYPES } from "../../helpers/activityLog";
import useCrudActions from "../../hooks/useCrudActions";
import { moveItem, sameOrder } from "./hotelPriorityRules";
import { removePriorityHotel, reorderPriority } from "./hotelPriorityService";

/**
 * The ranked hotels of one airport and crew type. With Edit rights: ▲ / ▼ change the order on the page (Save order / Cancel
 * then appear), Remove takes a hotel out (the next ones move up). History opens this list's changes in the side slider.
 */
function PriorityList({ airportCode, airportName, crewType, rows, canEdit, onChanged }) {
  const [draft, setDraft] = useState(rows);
  const [showHistory, setShowHistory] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const { busy, save, remove } = useCrudActions();
  const dirty = !sameOrder(rows, draft);

  useEffect(() => setDraft(rows), [rows]);

  const done = async () => {
    setHistoryKey((k) => k + 1);
    await onChanged();
  };
  const saveOrder = () => save(() => reorderPriority({ airportCode, crewType: crewType.code, hotelCodes: draft.map((r) => r.hotelCode) }), { onSuccess: done });
  const removeHotel = (row) =>
    remove(() => removePriorityHotel({ airportCode, id: row.id }), {
      confirmMessage: `Remove <b>${row.hotelName}</b> from the ${airportCode} ${crewType.label.toLowerCase()} list? The hotels after it move up.`,
      onSuccess: done,
    });

  return (
    <section className={`hbp-list${dirty ? " is-dirty" : ""}`} aria-label={`${airportCode} ${crewType.label}`}>
      <header className="hbp-list__head">
        <h3>
          <i className={crewType.icon} aria-hidden="true" /> {crewType.label}
        </h3>
        <button type="button" className="btn btn-link btn-sm hbp-history-btn" aria-haspopup="dialog" onClick={() => setShowHistory(true)}>
          <i className="fas fa-history" aria-hidden="true" /> History
        </button>
      </header>
      {draft.length ? (
        <ol className="hbp-rank">
          {draft.map((r, i) => (
            <li key={r.hotelCode} className="hbp-rank__item">
              <span className="hbp-rank__no">{i + 1}</span>
              <span className="hbp-rank__name" title={r.hotelCode}>
                {r.hotelName}
              </span>
              {canEdit && (
                <span className="hbp-rank__actions">
                  <button
                    type="button"
                    className="hbp-icon-btn"
                    aria-label={`Move ${r.hotelName} up`}
                    disabled={busy || i === 0}
                    onClick={() => setDraft(moveItem(draft, i, -1))}
                  >
                    <i className="fas fa-arrow-up" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="hbp-icon-btn"
                    aria-label={`Move ${r.hotelName} down`}
                    disabled={busy || i === draft.length - 1}
                    onClick={() => setDraft(moveItem(draft, i, 1))}
                  >
                    <i className="fas fa-arrow-down" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="hbp-icon-btn is-danger"
                    aria-label={`Remove ${r.hotelName}`}
                    disabled={busy || dirty}
                    title={dirty ? "Save or cancel the new order first" : "Remove"}
                    onClick={() => removeHotel(r)}
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                </span>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <p className="hbp-empty">No hotel in this list yet.</p>
      )}
      {dirty && (
        <div className="hbp-list__save">
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={saveOrder}>
            <i className={`fas fa-${busy ? "spinner fa-spin" : "check"}`} aria-hidden="true" /> Save order
          </button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setDraft(rows)}>
            Cancel
          </button>
        </div>
      )}
      {showHistory && (
        <MasterDrawer
          title={`${airportCode} · ${crewType.label} – change history`}
          subtitle={airportName}
          showSave={false}
          onClose={() => setShowHistory(false)}
        >
          <ActivityHistory logType={LOG_TYPES.hotelPriority} columnValue1={airportCode} columnValue2={crewType.code} reloadKey={historyKey} />
        </MasterDrawer>
      )}
    </section>
  );
}

/** One airport: its cockpit and cabin crew lists side by side, and Add hotel. */
export default function PriorityCard({ card, crewTypes, canEdit, onAdd, onChanged }) {
  return (
    <article className="hbp-card" aria-label={card.airportName}>
      <header className="hbp-card__head">
        <h2>
          <span className="hbp-card__code">{card.airportCode}</span> {card.airportName}
        </h2>
        {canEdit && (
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => onAdd(card.airportCode)}>
            <i className="fas fa-plus" aria-hidden="true" /> Add hotel
          </button>
        )}
      </header>
      <div className="hbp-card__lists">
        {crewTypes.map((ct) => (
          <PriorityList
            key={ct.code}
            airportCode={card.airportCode}
            airportName={card.airportName}
            crewType={ct}
            rows={card.lists[ct.code] ?? []}
            canEdit={canEdit}
            onChanged={onChanged}
          />
        ))}
      </div>
    </article>
  );
}
